import { motion } from "@/lib/motion";
import { OptimizedGlassCard } from "@/components/ui/OptimizedGlassCard";
import { ButtonCustom } from "@/components/ui/button-custom";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, User } from "lucide-react";
import blogPostsData from "@/content/blog-posts.json";
import authorsData from "@/content/authors.json";
import { getPost } from "@/lib/blogStorage";
import { useEffect, useMemo } from "react";
import { ReadingProgressBar } from "@/components/blog/ReadingProgressBar";
import { SocialShare } from "@/components/blog/SocialShare";
import { AuthorProfile } from "@/components/blog/AuthorProfile";
import Seo from "@/components/seo/Seo";

/** Author type definition - matches authors.json structure */
interface Author {
  id: string;
  name: string;
  role: string;
  avatar: string;
  bio: string;
  social?: {
    twitter?: string;
    linkedin?: string;
  };
}

interface BlogPostDetail {
  id: string;
  title: string;
  metaDescription: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  tags: string[];
  author: string;
  authorId?: string;
  intro: string;
  sections: Array<{
    title: string;
    content: string;
  }>;
  conclusion: string;
  faq: Array<{
    question: string;
    answer: string;
  }>;
  relatedPosts: string[];
}

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug
    ? ((blogPostsData as Record<string, BlogPostDetail>)[slug] ??
      (getPost(slug) as BlogPostDetail | null))
    : null;
  
  
  // Get author data with proper typing
  const authorInfo = useMemo<Author | null>(() => {
    if (!post?.authorId) return null;
    return (authorsData as Author[]).find(author => author.id === post.authorId) || null;
  }, [post?.authorId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-display font-bold mb-4">المقال غير موجود</h1>
          <ButtonCustom variant="foil" asChild>
            <Link to="/blog">
              العودة للمدونة
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </ButtonCustom>
        </div>
      </div>
    );
  }

  const relatedPostsData = post.relatedPosts
    .map(id => (blogPostsData as Record<string, BlogPostDetail>)[id])
    .filter(Boolean);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "image": {
      "@type": "ImageObject",
      "url": post.image.startsWith('http') ? post.image : `https://itqan.agency${post.image}`,
      "width": 800,
      "height": 600
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "author": authorInfo ? {
      "@type": "Person",
      "name": authorInfo.name,
      "jobTitle": authorInfo.role,
      "description": authorInfo.bio,
      "url": `https://itqan.agency/blog/${post.id}#author`,
      "sameAs": authorInfo.social ? [
        authorInfo.social.twitter,
        authorInfo.social.linkedin
      ].filter(Boolean) : []
    } : {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "إتقان",
      "logo": {
        "@type": "ImageObject",
        "url": "https://itqan.agency/brand/itqan-logo-main.jpg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://itqan.agency/blog/${post.id}`
    }
  };

  // Prepare OG image URL
  const ogImageUrl = post.image.startsWith('http') ? post.image : post.image;

  return (
    <>
      <ReadingProgressBar />
      <Seo
        title={`${post.title} | مدونة إتقان`}
        description={post.metaDescription}
        canonical={`/blog/${post.id}`}
        ogImage={ogImageUrl}
        ogType="article"
        jsonLd={[articleSchema, faqSchema]}
      />

      <article className="min-h-screen pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative py-16 overflow-hidden bg-surface">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-br from-sand via-transparent to-obs" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-foreground/60 mb-6">
                <Link to="/" className="hover:text-sand transition-colors">الرئيسية</Link>
                <ArrowLeft className="w-3 h-3" />
                <Link to="/blog" className="hover:text-sand transition-colors">المدونة</Link>
                <ArrowLeft className="w-3 h-3" />
                <span className="text-sand">{post.category}</span>
              </div>

              {/* Category Badge */}
              <div className="mb-4">
                <span className="glass px-4 py-2 text-sm text-sand rounded-full inline-block">
                  {post.category}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 foil">
                <strong>{post.title}</strong>
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-foreground/70 mb-8">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-sand" />
                  <span className="text-sm">{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-sand" />
                  <span className="text-sm">{new Date(post.date).toLocaleDateString('ar-SA')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-sand" />
                  <span className="text-sm">{post.readTime}</span>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full border border-sand/20 text-sand/80"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Social Share */}
              <div className="mt-8">
                <SocialShare title={post.title} url={`/blog/${post.id}`} />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="container mx-auto px-4 -mt-8 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-5xl mx-auto"
          >
            <div className="relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden">
              <img
                src={post.image}
                alt={post.imageAlt}
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obs/50 via-transparent to-transparent" />
            </div>
          </motion.div>
        </section>

        {/* Article Content */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <OptimizedGlassCard className="prose prose-lg prose-invert max-w-none">
              {/* Introduction */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-12"
              >
                <p className="text-xl leading-relaxed text-foreground/90 font-medium">
                  {post.intro}
                </p>
              </motion.div>

              {/* Sections */}
              {post.sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="mb-10"
                >
                  <h2 className="text-3xl font-display font-bold mb-4 text-sand">
                    <strong>{section.title}</strong>
                  </h2>
                  <p className="text-lg leading-relaxed text-foreground/80">
                    {section.content}
                  </p>
                </motion.div>
              ))}

              {/* Conclusion */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-12 pt-8 border-t border-sand/20"
              >
                <h2 className="text-3xl font-display font-bold mb-4 text-sand">
                  <strong>الخلاصة</strong>
                </h2>
                <p className="text-lg leading-relaxed text-foreground/80">
                  {post.conclusion}
                </p>
              </motion.div>
            </OptimizedGlassCard>

            {/* Author Profile */}
            {authorInfo && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-8"
              >
                <AuthorProfile author={authorInfo} />
              </motion.div>
            )}

            {/* Social Share (Bottom) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-8"
            >
              <SocialShare title={post.title} url={`/blog/${post.id}`} />
            </motion.div>

            {/* FAQ Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-12"
            >
              <OptimizedGlassCard>
                <h2 className="text-3xl font-display font-bold mb-8 text-sand flex items-center gap-3">
                  <Tag className="w-7 h-7" />
                  <strong>الأسئلة الشائعة</strong>
                </h2>
                <div className="space-y-6">
                  {post.faq.map((item, index) => (
                    <div key={index} className="pb-6 border-b border-sand/10 last:border-0">
                      <h3 className="text-xl font-bold mb-3 text-foreground">
                        <strong>{item.question}</strong>
                      </h3>
                      <p className="text-foreground/70 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </OptimizedGlassCard>
            </motion.div>

            {/* Related Posts */}
            {relatedPostsData.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-16"
              >
                <h2 className="text-3xl font-display font-bold mb-8 text-sand">
                  <strong>مقالات ذات صلة</strong>
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {relatedPostsData.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={`/blog/${relatedPost.id}`}
                      className="group"
                    >
                      <OptimizedGlassCard className="h-full hover:scale-[1.02] transition-transform duration-300">
                        <div className="relative h-48 -m-6 md:-m-8 mb-6 md:mb-8 overflow-hidden rounded-t-lg">
                          <img
                            src={relatedPost.image}
                            alt={relatedPost.imageAlt}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-obs via-obs/50 to-transparent" />
                          <div className="absolute top-4 right-4">
                            <span className="glass px-3 py-1 text-xs text-sand rounded-full">
                              {relatedPost.category}
                            </span>
                          </div>
                        </div>
                        <h3 className="text-xl font-display font-bold group-hover:text-sand transition-colors mb-3">
                          <strong>{relatedPost.title}</strong>
                        </h3>
                        <p className="text-foreground/70 text-sm leading-relaxed line-clamp-2 mb-4">
                          {relatedPost.metaDescription}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-foreground/60">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{new Date(relatedPost.date).toLocaleDateString('ar-SA')}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{relatedPost.readTime}</span>
                          </div>
                        </div>
                      </OptimizedGlassCard>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-16"
            >
              <OptimizedGlassCard className="text-center">
                <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 foil">
                  <strong>هل تريد تحسين نتائجك الرقمية؟</strong>
                </h2>
                <p className="text-lg text-foreground/70 mb-8">
                  احجز استشارة مجانية مع خبرائنا اليوم
                </p>
                <ButtonCustom variant="foil" size="lg" asChild>
                  <Link to="/contact">
                    احجز استشارتك المجانية
                    <ArrowLeft className="w-5 h-5" />
                  </Link>
                </ButtonCustom>
              </OptimizedGlassCard>
            </motion.div>

            {/* Back to Blog */}
            <div className="mt-12 text-center">
              <ButtonCustom variant="ghost" asChild>
                <Link to="/blog" className="inline-flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  العودة إلى المدونة
                </Link>
              </ButtonCustom>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default BlogDetail;
